<template>
  <l-table
    ref="LTable"
    :module-name="MODULE_NAME"
    :table-columns="visibleColumns"
    :filter-class="null"
  >
    <template #create-button>
      <b-button
        v-if="canCreate"
        v-b-toggle.sidebar-create-uz-asbo
        variant="primary"
      >
        <feather-icon
          icon="PlusIcon"
          class="mr-50"
        />
        <span>{{ $t('Create list') }}</span>
      </b-button>
      <b-button
        v-if="isInnovation"
        variant="outline-primary"
        class="ml-1"
        @click="$router.push({ name: 'uz-asbo-monthly' })"
      >
        <feather-icon
          icon="CalendarIcon"
          class="mr-50"
        />
        <span>{{ $t('Monthly summary') }}</span>
      </b-button>
    </template>

    <template #before-search>
      <v-select
        v-model="year"
        :options="years"
        :placeholder="$t('Year')"
        class="uz-asbo-filter mr-1"
        @input="applyFilters"
      />
      <v-select
        v-model="month"
        :options="months"
        :reduce="option => option.id"
        :get-option-label="option => $langName(option.name)"
        :placeholder="$t('Month')"
        class="uz-asbo-filter mr-1"
        @input="applyFilters"
      />
      <v-select
        v-model="status"
        :options="statusOptions"
        :reduce="option => option.id"
        :get-option-label="option => $langName(option.name)"
        :placeholder="$t('Status')"
        class="uz-asbo-filter mr-1"
        @input="applyFilters"
      />
    </template>

    <template #create-body>
      <create @refetchData="refetchData" />
      <review
        :uz-asbo-list="selectedList"
        :mode="reviewMode"
        @refetchData="refetchData"
      />
    </template>

    <template #cell(period)="{ data }">
      {{ data.item.year }} — {{ $langName(getMonthName(data.item.month)) }}
      <b-badge
        v-if="data.item.revision > 1"
        variant="light-secondary"
        class="ml-50"
      >
        {{ $t('Revision') }} {{ data.item.revision }}
      </b-badge>
    </template>

    <template #cell(organization_name)="{ data }">
      {{ data.value || '—' }}
    </template>

    <template #cell(status)="{ data }">
      <b-badge :variant="getStatus(data.value).variant">
        {{ $langName(getStatus(data.value).name) }}
      </b-badge>
    </template>

    <template #cell(org_head_comment)="{ data }">
      {{ data.item.innovation_comment || data.value || '—' }}
    </template>

    <template #cell(created_at)="{ data }">
      {{ formatDate(data.value) }}
    </template>

    <template #cell(actions)="{ data }">
      <div class="text-nowrap">
        <feather-icon
          v-b-tooltip.hover.top="$t('View')"
          icon="EyeIcon"
          class="cursor-pointer mr-1"
          size="16"
          @click="$router.push({ name: 'uz-asbo-view', params: { id: data.item.id } })"
        />
        <feather-icon
          v-if="canSend(data.item)"
          v-b-tooltip.hover.top="$t('Send to responsible head')"
          icon="SendIcon"
          class="cursor-pointer mr-1 text-primary"
          size="16"
          @click="send(data.item)"
        />
        <feather-icon
          v-if="canReview(data.item)"
          v-b-tooltip.hover.top="$t('Approve or reject')"
          v-b-toggle.sidebar-review-uz-asbo
          icon="CheckCircleIcon"
          class="cursor-pointer mr-1 text-success"
          size="16"
          @click="openReview(data.item, 'org-head')"
        />
        <feather-icon
          v-if="canInnovationReject(data.item)"
          v-b-tooltip.hover.top="$t('Reject')"
          v-b-toggle.sidebar-review-uz-asbo
          icon="XCircleIcon"
          class="cursor-pointer mr-1 text-danger"
          size="16"
          @click="openReview(data.item, 'innovation')"
        />
        <feather-icon
          v-if="canDelete(data.item)"
          v-b-tooltip.hover.top="$t('Delete')"
          icon="TrashIcon"
          class="cursor-pointer mr-1 text-danger"
          size="16"
          @click="remove(data.item)"
        />
      </div>
    </template>
  </l-table>
</template>

<script>
import {
  BButton, BBadge, VBToggle, VBTooltip,
} from 'bootstrap-vue'
import { onUnmounted } from '@vue/composition-api'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import store from '@/store'
import uzAsboModule from '@/store/uz-asbo'
import { isAdmin, isOrgHead, isRoleUniversity } from '@/auth/utils'
import LTable from '@/views/components/LTable.vue'
import Create from './Create.vue'
import Review from './Review.vue'
import config, {
  MONTHS, UZASBO_STATUSES, getMonthName, getUzAsboStatus, yearOptions,
} from './config'

export default {
  directives: {
    Ripple,
    'b-toggle': VBToggle,
    'b-tooltip': VBTooltip,
  },
  components: {
    BButton,
    BBadge,
    LTable,
    Create,
    Review,
    vSelect,
  },
  data() {
    return {
      year: null,
      month: null,
      status: null,
      selectedList: {},
      reviewMode: 'org-head',
      months: MONTHS,
      years: yearOptions(),
    }
  },
  computed: {
    isOrgAdmin() {
      return isRoleUniversity()
    },
    isInnovation() {
      return isAdmin()
    },
    isResponsibleHead() {
      return isOrgHead()
    },
    canCreate() {
      return this.isOrgAdmin
    },
    visibleColumns() {
      // Tashkilot o'z ro'yxatlarini ko'radi - tashkilot ustuni ortiqcha
      if (this.isInnovation) return this.tableColumns
      return this.tableColumns.filter(column => column.key !== 'organization_name')
    },
    statusOptions() {
      return Object.values(UZASBO_STATUSES).map(id => ({ id, ...getUzAsboStatus(id) }))
    },
  },
  created() {
    this.$store.commit(`${this.MODULE_NAME}/FRESH`)
  },
  methods: {
    getStatus(status) {
      return getUzAsboStatus(status)
    },
    getMonthName,
    formatDate(value) {
      if (!value) return '—'
      return new Date(value).toLocaleDateString()
    },
    applyFilters() {
      const params = this.$refs.LTable.extraParams
      const set = (key, value) => {
        if (value) params[key] = value
        else delete params[key]
      }
      set('year', this.year)
      set('month', this.month)
      set('status', this.status)
      this.refetchData()
    },
    canSend(item) {
      return this.isOrgAdmin && item.is_editable
    },
    canDelete(item) {
      return this.isOrgAdmin && item.is_editable
    },
    canReview(item) {
      return this.isResponsibleHead && item.status === UZASBO_STATUSES.SENT_TO_ORG_HEAD
    },
    canInnovationReject(item) {
      return this.isInnovation && item.status === UZASBO_STATUSES.APPROVED_BY_ORG_HEAD
    },
    openReview(item, mode) {
      this.selectedList = item
      this.reviewMode = mode
    },
    send(item) {
      this.$removeSwal(this, item.id, `${this.MODULE_NAME}/send`, this.refetchData, {
        title: 'Are you sure?',
        text: "Ro'yxat tashkilot ma'sul rahbariga yuboriladi",
        confirmButtonText: 'Send',
        successTitle: 'Sent',
        successText: 'Successfully completed',
        errorText: 'There was an error in successful execution',
      })
    },
    remove(item) {
      this.$removeSwal(this, item, `${this.MODULE_NAME}/del`, this.refetchData)
    },
    refetchData() {
      this.$refs.LTable.refetchData()
    },
  },
  setup() {
    const MODULE_NAME = 'uzAsbo'
    if (!store.hasModule(MODULE_NAME)) store.registerModule(MODULE_NAME, uzAsboModule)

    onUnmounted(() => {
      if (store.hasModule(MODULE_NAME)) store.unregisterModule(MODULE_NAME)
    })

    return {
      MODULE_NAME,
      ...config(),
    }
  },
}
</script>

<style scoped>
.uz-asbo-filter {
  min-width: 160px;
}
</style>
